import { ChangeEvent, ClipboardEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { fetchGoals, fetchTeam, fetchMeta, fetchWorkLogs, saveWorkLogs, uploadFile } from '../utils/api'
import { getCurrentUser } from '../utils/auth'
import type { GoalItem, GoalStatus, MemberProfile, WorkLog, WorkLogAttachment } from '../types/okr'

const emptyForm = {
  id: '',
  date: new Date().toISOString().slice(0, 10),
  title: '',
  project: '日常工作',
  content: '',
  progress: 0,
  timeSpent: 1,
  status: 'in_progress' as GoalStatus,
  linkedGoalIds: [] as string[],
  tags: '',
  nextAction: '',
  blocker: '',
  yuqueLink: '',
  source: '系统维护',
  ownerId: '',
  ownerName: ''
}

const createId = () => `wl-${Date.now()}`

function WorkLogs() {
  const currentUser = useMemo(() => getCurrentUser(), [])
  const [ownerId, setOwnerId] = useState('GS2480')
  const [worklogs, setWorklogs] = useState<WorkLog[]>([])
  const [goals, setGoals] = useState<GoalItem[]>([])
  const [members, setMembers] = useState<MemberProfile[]>([])
  const [memberFilter, setMemberFilter] = useState('')
  const [form, setForm] = useState(emptyForm)
  const [attachments, setAttachments] = useState<WorkLogAttachment[]>([])
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'team' | 'mine'>('mine')

  const isOwner = Boolean(currentUser.userId && ownerId && currentUser.userId.toLowerCase() === ownerId.toLowerCase())
  const isMine = (log: WorkLog) => log.ownerId === currentUser.userId || log.ownerName === currentUser.name

  useEffect(() => {
    const load = async () => {
      try {
        const [logs, goalList] = await Promise.all([fetchWorkLogs(), fetchGoals()])
        setWorklogs(logs)
        setGoals(goalList)
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载工作日志失败')
      }
    }

    load()
  }, [])

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const meta = await fetchMeta()
        if (meta?.ownerId) setOwnerId(meta.ownerId)
      } catch {
        // 静默降级
      }
    }
    loadMeta()
  }, [])

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const team = await fetchTeam()
        const normalized = [team.owner, ...(team.members || [])].filter(Boolean).reduce((acc: MemberProfile[], item) => {
          if (!acc.find((m) => m.userId === item.userId)) acc.push(item)
          return acc
        }, [])
        setMembers(normalized)
      } catch {
        // 忽略失败
      }
    }
    loadTeam()
  }, [ownerId, currentUser.userId])

  useEffect(() => {
    setViewMode(isOwner ? 'team' : 'mine')
  }, [isOwner])

  const filteredWorklogs = useMemo(() => {
    const base = viewMode === 'mine' ? worklogs.filter(isMine) : worklogs
    return memberFilter ? base.filter((log) => log.ownerId === memberFilter || log.ownerName === memberFilter) : base
  }, [worklogs, viewMode, currentUser, memberFilter])

  const stats = useMemo(() => ({
    count: filteredWorklogs.length,
    hours: filteredWorklogs.reduce((sum, item) => sum + Number(item.timeSpent || 0), 0),
    blocked: filteredWorklogs.filter((item) => item.status === 'blocked').length
  }), [filteredWorklogs])

  const canEdit = isOwner || !form.id || form.ownerId === currentUser.userId || !form.ownerId

  const uploadAttachment = async (file: File) => {
    setUploading(true)
    setError('')
    try {
      const result = await uploadFile(file)
      setAttachments((current) => [...current, { name: result.fileName, url: result.filePath }])
      setMessage('附件上传成功')
    } catch (err) {
      setError(err instanceof Error ? err.message : '附件上传失败')
    } finally {
      setUploading(false)
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (form.id && !isOwner && form.ownerId && form.ownerId !== currentUser.userId) {
      setError('仅可编辑自己的日志')
      return
    }
    if (!form.title.trim()) return setError('标题必填')
    if (form.status === 'blocked' && !form.blocker.trim()) return setError('请填写阻塞说明')

    setSaving(true)
    setError('')
    setMessage('')
    const timestamp = new Date().toISOString()

    const payload: WorkLog = {
      id: form.id || createId(),
      date: form.date,
      title: form.title,
      project: form.project || '日常工作',
      content: form.content,
      progress: Math.min(100, Math.max(0, Number(form.progress))),
      timeSpent: Number(form.timeSpent),
      status: form.status,
      linkedGoalIds: form.linkedGoalIds,
      tags: [],
      nextAction: '',
      blocker: form.blocker,
      yuqueLink: form.yuqueLink,
      attachments,
      ownerId: currentUser.userId,
      ownerName: currentUser.name,
      source: form.source,
      createdAt: timestamp,
      updatedAt: timestamp
    }

    const nextLogs = form.id
      ? worklogs.map((item) => item.id === form.id ? { ...payload, createdAt: item.createdAt, updatedAt: timestamp } : item)
      : [payload, ...worklogs]

    try {
      await saveWorkLogs(nextLogs)
      setWorklogs(nextLogs)
      setForm(emptyForm)
      setAttachments([])
      setMessage('工作日志已保存')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleAttachmentUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await uploadAttachment(file)
    event.target.value = ''
  }

  const handlePaste = async (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items
    if (!items) return
    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'))
    if (!imageItem) return
    const file = imageItem.getAsFile()
    if (!file) return
    event.preventDefault()
    await uploadAttachment(file)
  }

  const editLog = (log: WorkLog) => {
    if (!isOwner && !isMine(log)) {
      setError('仅可编辑自己的日志')
      return
    }
    setForm({
      id: log.id,
      date: log.date,
      title: log.title,
      project: log.project || '日常工作',
      content: log.content,
      progress: log.progress,
      timeSpent: log.timeSpent,
      status: log.status,
      linkedGoalIds: log.linkedGoalIds,
      tags: log.tags.join(', '),
      nextAction: log.nextAction || '',
      blocker: log.blocker || '',
      yuqueLink: log.yuqueLink || '',
      source: log.source || '系统维护',
      ownerId: log.ownerId || currentUser.userId,
      ownerName: log.ownerName || currentUser.name
    })
    setAttachments(log.attachments || [])
  }

  const removeLog = async (id: string) => {
    const target = worklogs.find((w) => w.id === id)
    if (!target) return
    if (!isOwner && !isMine(target)) {
      setError('仅可删除自己的日志')
      return
    }
    const nextLogs = worklogs.filter((item) => item.id !== id)
    try {
      await saveWorkLogs(nextLogs)
      setWorklogs(nextLogs)
      setMessage('日志已删除')
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败')
    }
  }

  const toggleGoal = (goalId: string) => {
    setForm((current) => ({
      ...current,
      linkedGoalIds: current.linkedGoalIds.includes(goalId)
        ? current.linkedGoalIds.filter((item) => item !== goalId)
        : [...current.linkedGoalIds, goalId]
    }))
  }

  const markStatus = (status: GoalStatus, blockerHint?: string) => {
    setForm((current) => ({
      ...current,
      status,
      blocker: status === 'blocked' && !current.blocker ? (blockerHint || '') : current.blocker
    }))
  }

  return (
    <div className="page-shell">
      <section className="page-header-block">
        <div>
          <span className="section-kicker">执行沉淀</span>
          <h2>工作日志</h2>
          <p>支持文字记录、目标关联、附件上传、语雀链接沉淀，已自动导入你最近一周的重要工作。</p>
        </div>
        <div className="page-header-actions">
          {isOwner ? (
            <>
              <div className="segmented-control" role="group" aria-label="视图切换">
                <button type="button" className={viewMode === 'team' ? 'active' : ''} onClick={() => setViewMode('team')}>团队</button>
                <button type="button" className={viewMode === 'mine' ? 'active' : ''} onClick={() => setViewMode('mine')}>我的</button>
              </div>
              {viewMode === 'team' && members.length > 0 && (
                <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="select-lite">
                  <option value="">全部成员</option>
                  {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </select>
              )}
            </>
          ) : (
            <div className="segmented-control" role="group" aria-label="视图切换">
              <button type="button" className="active" disabled>我的</button>
            </div>
          )}
          {message && <span className="success-text">{message}</span>}
          {error && <span className="error-text">{error}</span>}
        </div>
      </section>

      {!isOwner && (
        <div className="info-banner">
          <strong>成员自助：</strong> 你可以提交/编辑自己的日志，负责人 {ownerId} 可查看全员。
        </div>
      )}

      <section className="stats-grid compact-stats">
        <article className="stat-card-modern"><span>日志总数</span><strong>{stats.count}</strong></article>
        <article className="stat-card-modern"><span>累计工时</span><strong>{stats.hours.toFixed(1)}h</strong></article>
        <article className="stat-card-modern"><span>阻塞事项</span><strong>{stats.blocked}</strong></article>
      </section>

      <section className="content-grid two-columns align-start">
        <form className="card-panel form-panel" onSubmit={submit}>
          <div className="panel-header"><h3>{form.id ? '编辑日志' : '新增日志'}</h3></div>
          <div className="form-grid">
            <label>
              <span>日期</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required disabled={!canEdit} />
            </label>
            <label className="full-span">
              <span>标题</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required disabled={!canEdit} />
            </label>
            <label className="full-span">
              <span>内容</span>
              <textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} onPaste={handlePaste} required disabled={!canEdit} />
            </label>
            <label>
              <span>进度</span>
              <input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} disabled={!canEdit} />
            </label>
            <label>
              <span>工时</span>
              <input type="number" min="0.5" step="0.5" value={form.timeSpent} onChange={(e) => setForm({ ...form, timeSpent: Number(e.target.value) })} disabled={!canEdit} />
            </label>
            <label>
              <span>状态</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as GoalStatus })} disabled={!canEdit}>
                <option value="in_progress">进行中</option>
                <option value="completed">已完成</option>
                <option value="blocked">阻塞</option>
                <option value="not_started">未开始</option>
              </select>
            </label>
            <label>
              <span>语雀链接</span>
              <input value={form.yuqueLink} onChange={(e) => setForm({ ...form, yuqueLink: e.target.value })} placeholder="https://www.yuque.com/..." disabled={!canEdit} />
            </label>
            <label className="full-span">
              <span>阻塞说明</span>
              <textarea rows={2} value={form.blocker} onChange={(e) => setForm({ ...form, blocker: e.target.value })} placeholder="若状态为阻塞，请说明原因与依赖" disabled={!canEdit} />
            </label>
          </div>

          <div className="chips-row">
            <span>快捷状态：</span>
            <button type="button" className="pill" onClick={() => markStatus('in_progress')}>进行中</button>
            <button type="button" className="pill" onClick={() => markStatus('completed')}>已完成</button>
            <button type="button" className="pill warning" onClick={() => markStatus('blocked', '需协助解除阻塞')}>标记阻塞</button>
          </div>

          <div className="goal-selector">
            <h4>关联目标</h4>
            <div className="tag-row">
              {goals.map((goal) => (
                <button
                  type="button"
                  key={goal.id}
                  className={`tag-select ${form.linkedGoalIds.includes(goal.id) ? 'active' : ''}`}
                  onClick={() => toggleGoal(goal.id)}
                  disabled={!canEdit}
                >
                  {goal.title}
                </button>
              ))}
            </div>
          </div>

          <div className="attachment-panel">
            <h4>附件上传 / 粘贴图片</h4>
            <input type="file" onChange={handleAttachmentUpload} disabled={uploading || !canEdit} />
            {uploading && <p>上传中...</p>}
            <ul className="bullet-list compact">
              {attachments.map((item) => (
                <li key={item.url}>
                  <a href={item.url} target="_blank" rel="noreferrer">{item.name}</a>
                </li>
              ))}
            </ul>
          </div>

          <button className="primary-btn" disabled={saving || !canEdit}>{saving ? '保存中...' : '保存工作日志'}</button>
        </form>

        <div className="card-panel">
          <div className="panel-header"><h3>日志时间线</h3></div>
          <div className="stack-list">
            {filteredWorklogs.map((log) => (
              <div key={log.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{log.title}</strong>
                    <p>{log.date} · {log.timeSpent}h {log.ownerName ? `· ${log.ownerName}` : ''}</p>
                  </div>
                  <div className="inline-actions">
                    <span className={`status-badge ${log.status}`}>{log.status}</span>
                    <button className="text-btn" disabled={!isOwner && !isMine(log)} onClick={() => editLog(log)}>编辑</button>
                    <button className="text-btn danger" disabled={!isOwner && !isMine(log)} onClick={() => removeLog(log.id)}>删除</button>
                  </div>
                </div>
                <p>{log.content}</p>
                {log.blocker && log.blocker !== '无' && <p className="error-text"><strong>阻塞：</strong>{log.blocker}</p>}
                {log.attachments?.length > 0 && (
                  <ul className="bullet-list compact">
                    {log.attachments.map((item) => (
                      <li key={item.url}><a href={item.url} target="_blank" rel="noreferrer">{item.name}</a></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {filteredWorklogs.length === 0 && <div className="empty-state">暂无数据</div>}
          </div>
        </div>
      </section>
    </div>
  )
}

export default WorkLogs
